import { Promise } from 'bluebird';
import { useState, useCallback, useContext, useMemo, useEffect, useRef } from 'react';
import _ from 'underscore';

import {
  DataServiceContext,
  InstrumentationServiceContext,
  useIsSmartPageTemplateSuggestionEnabled,
} from '@bingads-webui-campaign-react/smart-page-context';

import { SmartPageTemplate } from './consts';

const DEBOUNCE_WAIT_IN_MS = 500;
const emptyArray = [];
const defaultSuggestions = [SmartPageTemplate.Default];

export function useTemplateSuggestion(categories = emptyArray) {
  const [suggestions, setSuggestions] = useState(defaultSuggestions);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const previousCategories = useRef(null);
  const isSmartPageTemplateSuggestionEnabled = useIsSmartPageTemplateSuggestionEnabled();

  const { smartPageService } = useContext(DataServiceContext);
  const logger = useContext(InstrumentationServiceContext);

  const getTemplateSuggestions = useCallback((cbCategories) => {
    const finalCategories = cbCategories || categories;
    previousCategories.current = finalCategories;

    if (finalCategories.length > 0) {
      setIsLoading(true);
      return smartPageService.getTemplateSuggestions(finalCategories)
        .then((res) => {
          setSuggestions(res);
          logger.logTrace(`Successfully get template suggestion for categories: ${finalCategories}, suggestion: ${res}`);

          return res;
        })
        .catch((err) => {
          setError(err);
          logger.logError('Error while getting template suggestion', err);

          throw err;
        })
        .finally(() => {
          setIsLoading(false);
        });
    }

    setSuggestions(defaultSuggestions);

    return Promise.resolve(defaultSuggestions);
  }, [categories, smartPageService, logger]);

  const debouncedGetTemplateSuggestion = useMemo(
    () => _.debounce(getTemplateSuggestions, DEBOUNCE_WAIT_IN_MS),
    [getTemplateSuggestions]
  );

  useEffect(() => {
    const shouldGetSuggestion = isSmartPageTemplateSuggestionEnabled
      && (!previousCategories.current
        || previousCategories.current.toString() !== categories.toString());

    if (shouldGetSuggestion) {
      if (_.isEmpty(categories)) {
        setSuggestions(defaultSuggestions);
        previousCategories.current = categories;
      } else {
        debouncedGetTemplateSuggestion();
      }
    }

    return () => debouncedGetTemplateSuggestion.cancel();
  }, [categories, debouncedGetTemplateSuggestion, isSmartPageTemplateSuggestionEnabled]);

  return {
    suggestions,
    error,
    isLoading,
    getTemplateSuggestions,
  };
}
